import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PhotoPicker } from "./shared/PhotoPicker";
import { PhotoThumb } from "./shared/PhotoThumb";
import { MachineSelect } from "./shared/MachineSelect";
import { FilterBar } from "./shared/FilterBar";
import { useMyAssets } from "./shared/useMyAssets";
import { NoAccess } from "./shared/NoAccess";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationBar } from "@/components/shared/PaginationBar";

const VIEW_ROLES = ["Director", "Manager", "Accounts", "Back Office"];

function SubmitForm() {
  const { assets, needsPicker } = useMyAssets();
  const [machineAssetId, setMachineAssetId] = useState("");
  const [pieces, setPieces] = useState("");
  const [remarks, setRemarks] = useState("");
  const [oldPhotos, setOldPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (needsPicker && !machineAssetId) return toast.error("Please select which machine this entry is for.");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("pieces_used", pieces);
    if (remarks) fd.append("remarks", remarks);
    if (machineAssetId) fd.append("machine_asset_id", machineAssetId);
    oldPhotos.forEach((f) => fd.append("old_bullet_photos", f));
    newPhotos.forEach((f) => fd.append("new_bullet_photos", f));
    try {
      await client.post("/fleetcare/bullet-consumption", fd);
      toast.success("Bullet Consumption entry submitted");
      setPieces("");
      setRemarks("");
      setOldPhotos([]);
      setNewPhotos([]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="bullet-consumption-submit-form">
      {needsPicker && <MachineSelect assets={assets} value={machineAssetId} onChange={setMachineAssetId} testId="bullet-consumption-machine-select" />}
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">
          Pieces Used <span className="text-red-500">*</span>
        </p>
        <Input type="number" min="1" data-testid="bullet-consumption-pieces-input" value={pieces} onChange={(e) => setPieces(e.target.value)} required />
      </div>
      <PhotoPicker label="Old Bullet Photo(s)" required files={oldPhotos} onChange={setOldPhotos} testId="bullet-consumption-old-photo-picker" />
      <PhotoPicker label="New Bullet Photo(s)" required files={newPhotos} onChange={setNewPhotos} testId="bullet-consumption-new-photo-picker" />
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
        <Textarea data-testid="bullet-consumption-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>
      <Button type="submit" data-testid="bullet-consumption-submit-button" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
        {submitting ? "Submitting..." : "Submit Bullet Consumption"}
      </Button>
    </form>
  );
}

function DetailPanel({ entry, onClose }) {
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="bullet-consumption-detail-panel">
        <SheetHeader>
          <SheetTitle>{entry.asset_label}</SheetTitle>
          <SheetDescription>
            {entry.employee_name} · {entry.pieces_used} pieces · {new Date(entry.created_at).toLocaleString("en-IN")}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono mb-2">Old Bullet Photos</p>
            <div className="flex gap-2 flex-wrap">
              {(entry.old_bullet_photos || []).map((p, i) => (
                <PhotoThumb
                  key={i}
                  path={`/fleetcare/bullet-consumption/${entry.id}/photos/old_bullet_photos/${i}`}
                  alt={p.filename}
                  testId={`bullet-consumption-old-photo-${i}`}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono mb-2">New Bullet Photos</p>
            <div className="flex gap-2 flex-wrap">
              {(entry.new_bullet_photos || []).map((p, i) => (
                <PhotoThumb
                  key={i}
                  path={`/fleetcare/bullet-consumption/${entry.id}/photos/new_bullet_photos/${i}`}
                  alt={p.filename}
                  testId={`bullet-consumption-new-photo-${i}`}
                />
              ))}
            </div>
          </div>
          {entry.remarks && (
            <p className="text-sm">
              <span className="text-slate-500">Remarks:</span> {entry.remarks}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ViewList() {
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", assetId: "" });
  const params = {};
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.assetId) params.asset_id = filters.assetId;
  const { items: entries, loading, page, setPage, totalPages } = usePaginatedList("/fleetcare/bullet-consumption", params);
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-4" data-testid="bullet-consumption-view">
      <FilterBar {...filters} onChange={(v) => setFilters((f) => ({ ...f, ...v }))} testIdPrefix="bullet-consumption" />
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="bullet-consumption-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Pieces Used</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  No entries found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`bullet-consumption-row-${e.id}`} className="cursor-pointer" onClick={() => setSelected(e)}>
                  <TableCell>{new Date(e.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="font-mono text-xs">{e.asset_label}</TableCell>
                  <TableCell>{e.employee_name}</TableCell>
                  <TableCell className="font-mono">{e.pieces_used}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} testId="bullet-consumption-pagination" />
      {selected && <DetailPanel entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default function BulletConsumptionTab() {
  const { user } = useAuth();
  if (user?.role === "Operator") return <SubmitForm />;
  if (VIEW_ROLES.includes(user?.role)) return <ViewList />;
  return <NoAccess />;
}
